/**
 * ConnectionSelector
 *
 * Dropdown that allows switching between active connected connections.
 * Always renders a Select dropdown (with an up/down glyph) — never static text.
 */

import { useMemo, useState } from 'react';
import { Server, ChevronsUpDown, Plus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../../../../components/ui/select';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { ConnectionColorSwatch } from '../../../connections/components/ConnectionColorSwatch';
import { SELECTOR_TRIGGER_CLASS, SelectorTriggerTooltip } from './SelectorTriggerTooltip';
import { safeConnectionName, safeConnectionServer, safeServerText } from '../../../../types/safeConnectionDisplay';

/** Sentinel value used as Select value when no connection is set */
const NONE_VALUE = '__none__';
/** Sentinel value for the "Add connection..." option */
const ADD_NEW_VALUE = '__add_new__';

type ConnectionSelectorProps = {
  connectionId?: string;
  connectionName?: string;
  serverHost?: string;
  onConnectionChange: (connectionId: string, connectionName: string, serverHost: string) => void;
  /** When true, show all active connections regardless of engine (e.g. for notebooks). */
  showAllEngines?: boolean;
  /** Callback to open the "New Connection" dialog. If set, an "Add connection..." item is shown. */
  onAddConnection?: () => void;
  /** Connection colour. When set, a colour square replaces the Server icon. */
  color?: string;
  /**
   * Where the host goes when the connection has an alias: after the name
   * (default), or into the tooltip — the Query Editor status bar keeps the
   * trigger short there.
   */
  hostPlacement?: 'inline' | 'tooltip';
  /** Tooltip line naming what the trigger does. No tooltip when absent. */
  actionHint?: string;
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

/** When the connection has no user-set alias, just show the host */
function displayLabel(name?: string, host?: string): string {
  if (!name || name === 'Unnamed Connection') return host || 'Not connected';
  return name;
}

/** Whether to show the host in parentheses after the label */
function showHostSuffix(name?: string, host?: string): boolean {
  return !!host && !!name && name !== 'Unnamed Connection';
}

export function ConnectionSelector({
  connectionId,
  connectionName: rawConnectionName,
  serverHost: rawServerHost,
  onConnectionChange,
  showAllEngines = false,
  onAddConnection,
  color,
  hostPlacement = 'inline',
  actionHint,
  onOpenChange,
  className = '',
}: ConnectionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  // A tab's name / host can be text copied out of an older record or a session file: never shown raw (security slice).
  const connectionName = rawConnectionName && safeServerText(rawConnectionName);
  const serverHost = rawServerHost && safeServerText(rawServerHost);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const connections = useConnectionStore((s) => s.connections);

  // Determine the engine of the current connection
  const currentEngine = useMemo(() => {
    if (!connectionId) return null;
    const saved = connections.find((c) => c.id === connectionId);
    if (saved) return saved.engine ?? 'mssql';
    const active = activeConnections.find((c) => c.id === connectionId);
    return active?.connection.engine ?? 'mssql';
  }, [connectionId, connections, activeConnections]);

  // Get eligible connections: active, connected, optionally same engine
  const eligibleConnections = useMemo(() => {
    return activeConnections.filter((ac) => {
      if (ac.status !== 'connected') return false;
      if (ac.id === connectionId) return false;
      if (!showAllEngines && currentEngine) {
        const engine = ac.connection.engine ?? 'mssql';
        if (engine !== currentEngine) return false;
      }
      return true;
    });
  }, [activeConnections, currentEngine, connectionId, showAllEngines]);

  const handleChange = (value: string) => {
    if (value === NONE_VALUE) return;
    if (value === ADD_NEW_VALUE) {
      onAddConnection?.();
      return;
    }
    const selected = activeConnections.find((ac) => ac.id === value);
    if (!selected) return;
    onConnectionChange(selected.id, safeConnectionName(selected.connection), safeConnectionServer(selected.connection));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const colorOf = (id: string) => connections.find((c) => c.id === id)?.color;
  const currentColor = connectionId ? colorOf(connectionId) : undefined;

  const selectValue = connectionId ?? NONE_VALUE;
  const label = displayLabel(connectionName, serverHost);
  const hasHostSuffix = showHostSuffix(connectionName, serverHost);

  const tooltipContent = actionHint ? (
    <span className="flex flex-col gap-0.5">
      {connectionId && hostPlacement === 'tooltip' && (
        <span className="font-medium">{hasHostSuffix ? `${label} (${serverHost})` : label}</span>
      )}
      <span>{actionHint}</span>
    </span>
  ) : undefined;

  return (
    <Select value={selectValue} onValueChange={handleChange} open={isOpen} onOpenChange={handleOpenChange}>
      <SelectorTriggerTooltip content={tooltipContent} selectOpen={isOpen}>
        <SelectTrigger
          data-testid="connection-selector-trigger"
          className={`${SELECTOR_TRIGGER_CLASS} text-muted-foreground ${className}`}
        >
          {color ? (
            <ConnectionColorSwatch color={color} />
          ) : (
            <Server className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
          )}
          <span data-testid="status-bar-connection-name" className="truncate">
            {label}
          </span>
          {hasHostSuffix && hostPlacement === 'inline' && (
            // Cap + truncate so a long host (notably SQLite file paths) doesn't
            // expand the trigger past the available status-bar width. Without
            // this, the row-count flex sibling gets squeezed to offsetWidth: 0
            // and any `:visible`-based test locator silently misses it.
            <span className="text-muted-foreground truncate max-w-[200px]" title={serverHost}>
              ({serverHost})
            </span>
          )}
          {/* Up/down glyph: the list opens upward from the status bar, so a
              down chevron pointed away from it. */}
          <ChevronsUpDown className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
        </SelectTrigger>
      </SelectorTriggerTooltip>
      <SelectContent
        data-testid="connection-selector-content"
        className="max-h-60 overflow-auto min-w-[140px]"
        position="popper"
        side="top"
        align="start"
      >
        {/* Current connection (or "Not connected" placeholder) */}
        <SelectItem value={selectValue} className="text-xs">
          {currentColor && <ConnectionColorSwatch color={currentColor} className="inline-block mr-1.5" />}
          {connectionId
            ? (label || connectionId)
            : 'Not connected'}
          {connectionId && showHostSuffix(connectionName, serverHost) && (
            <span className="opacity-60 ml-1">({serverHost})</span>
          )}
        </SelectItem>
        {/* Other eligible connections */}
        {eligibleConnections.map((ac) => {
          const name = safeConnectionName(ac.connection);
          const itemColor = colorOf(ac.id);
          return (
            <SelectItem key={ac.id} value={ac.id} className="text-xs">
              {itemColor && <ConnectionColorSwatch color={itemColor} className="inline-block mr-1.5" />}
              {name}
              <span className="opacity-60 ml-1">
                ({safeConnectionServer(ac.connection)})
              </span>
            </SelectItem>
          );
        })}
        {/* Add connection option */}
        {onAddConnection && (
          <SelectItem value={ADD_NEW_VALUE} className="text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Plus className="w-3 h-3" />
              Add connection...
            </span>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
